<template>
  <WrappedForm
    v-if="show"
    ref="form"
    class="job-base-info"
    label-width="200px"
    :model="stepData"
    :formLines="formLines"
    :disabled="disabled"
  >
    <template #addTagButton>
      <el-button v-if="hasTagManagementPermission" class='add-tag-btn' type="text" @click="handleAddTagClick">{{ $t(`${i18nPrefix}.btn.addTag`) }}</el-button>
    </template>
    <template #location="{ lineName }">
      <LocationEditor v-model="stepData[lineName]" class="not-form-item" :disabled="disabled" :company-id="stepData.company"></LocationEditor>
    </template>
  </WrappedForm>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import mixin from './mixin'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { createI18nProperty, addI18nProperty } from '@/i18n'
import LocationEditor from './locationEditor.vue'
import { VISIBILITY_TYPE, ROLE_TYPE } from '@/constant/job'
import { cloneSelectedQuestionSubtrees } from '@/utils/openQuestionTree'

export default {
  mixins: [mixin],
  components: {
    WrappedForm,
    LocationEditor
  },
  props: {
    company: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      jobTemplateList: [],
      companyJobTemplateListLoader: null,
      companyJobTemplateList: [],
      creatorList: [],
      jobMemberList: [],
      experienceList: [],
      currencyList: [],
      salaryList: [],
      workTimeList: [],
      industryList: [],
      industryListLoader: null,
      stepData: { location: [] }
    }
  },
  computed: {
    isNotEditableJobTemplate () {
      const templateId = this.stepData.template
      let template = { id: null, job_property: null }
      if (templateId) {
        template = _.find(this.jobTemplateList, { id: templateId })
      }
      return template && template.job_property === '0'
    },
    formLines () {
      const vm = this
      const isTemplate = this.isTemplate
      const isEditMode = this.isEditMode
      const requiredMessage = this.$t('common.message.requiredFormField')
      const requiredRule = [{ required: true, message: requiredMessage, trigger: 'change' }]
      const lines = {
        isEditableTemplate: {
          elType: 'el-radio-group',
          label: this.$t(`${this.i18nPrefix}.label.isEditableTemplate`),
          option: {
            data: [
              { label: this.$t(`${this.i18nPrefix}.label.editable`), value: true },
              { label: this.$t(`${this.i18nPrefix}.label.notEditable`), value: false }
            ]
          },
          rules: requiredRule
        },
        template: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.jobTemplate`),
          option: {
            delayLoadData: this.loadCompanyJobTemplateList,
            grouped: true,
            groupOptionsDataKey: 'templateList',
            valueKey: 'id'
          },
          props: { clearable: true, filterable: true },
          events: { change: this.handleTemplateChange }
        },
        company: {
          elType: 'el-select',
          disabled: isTemplate && isEditMode,
          label: this.$t(`${this.i18nPrefix}.label.company`),
          option: {
            delayLoadData: this.loadCompanyList,
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          clearOnChange: ['creator', 'jobMember'],
          props: { filterable: true },
          rules: requiredRule
        },
        creator: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.jobCreator`),
          option: {
            delayLoadData: this.loadCreatorList,
            valueKey: 'id',
            labelKey: 'text'
          },
          props: { filterable: true }
        },
        jobMember: {
          hidden: isTemplate,
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.jobMember`),
          option: {
            delayLoadData: this.loadJobMember,
            valueKey: 'id',
            labelKey: 'text'
          },
          props: { clearable: true, filterable: true, multiple: true }
        },
        storeFront: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.storeFront`),
          option: {
            delayLoadData: this.loadStoreFront
          },
          props: { clearable: true, filterable: true }
        },
        cnName: {
          label: this.$t(`${this.i18nPrefix}.label.jobCnName`),
          rules: requiredRule
        },
        enName: {
          label: this.$t(`${this.i18nPrefix}.label.jobEnName`)
        },
        visibility: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.jobVisibility`),
          option: {
            data: _.map(VISIBILITY_TYPE, (value, typeName) => {
              const option = { value, typeName }
              createI18nProperty(option, 'label', `${this.i18nPrefix}.visibilityType.${typeName}`)
              return option
            })
          }
        },
        atsId: {
          label: this.$t(`${this.i18nPrefix}.label.atsId`)
        },
        tag: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.tag`),
          class: 'job-tag-line',
          option: {
            disableCacheLoadData: true,
            delayLoadData: this.loadJobTagList,
            valueKey: 'id',
            labelKey: 'name'
          },
          props: { clearable: true, filterable: true, multiple: true },
          contentExtraSlot: 'addTagButton'
        },
        visibleOnlyMember: {
          elType: 'el-radio-group',
          label: this.$t(`${this.i18nPrefix}.label.visibleOnlyMember`),
          option: {
            data: [{ label: this.$t('common.label.yes'), value: true }, { label: this.$t('common.label.no'), value: false }]
          }
        },
        requireExperience: {
          elType: 'el-radio-group',
          label: this.$t(`${this.i18nPrefix}.label.requireExperience`),
          option: {
            data: [
              { label: this.$t(`${this.i18nPrefix}.experienceType.graduate`), value: false },
              { label: this.$t(`${this.i18nPrefix}.experienceType.experienced`), value: true }
            ]
          }
        },
        roleType: {
          elType: 'el-radio-group',
          label: this.$t(`${this.i18nPrefix}.label.roleType`),
          option: {
            data: _.map(ROLE_TYPE, (value, key) => { return { label: this.$t(`${this.i18nPrefix}.roleType.${key}`), value } })
          },
          rules: requiredRule
        },
        location: {
          label: this.$t(`${this.i18nPrefix}.label.location`),
          contentSlot: 'location',
          rules: requiredRule
        },
        experience: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.experience`),
          option: {
            delayLoadData: this.loadExperienceList
          },
          rules: requiredRule
        },
        currencyAndSalary: {
          label: this.$t(`${this.i18nPrefix}.label.salary`),
          class: 'salary-form-line',
          hiddenPropName: true,
          required: true,
          multiple: {
            currency: {
              elType: 'el-select',
              class: 'currency-select',
              props: { clearable: true },
              option: {
                delayLoadData: this.loadCurrencyList
              }
            },
            salary: {
              elType: 'el-select',
              class: 'salary-select',
              props: { clearable: true },
              option: {
                delayLoadData: this.loadSalaryList
              },
              rules: requiredRule
            }
          }
        },
        workTime: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.workTime`),
          option: {
            delayLoadData: this.loadWorkTimeList
          }
        },
        industryAndJobType: {
          label: this.$t(`${this.i18nPrefix}.label.industryType`),
          class: 'salary-form-line',
          hiddenPropName: true,
          required: false,
          multiple: {
            industry: {
              elType: 'el-select',
              class: 'industry-select',
              option: {
                delayLoadData: this.loadIndustryList
              },
              props: { clearable: true },
              clearOnChange: ['jobType'],
              filterMethod: this.searchIndustryAndJobType
            },
            jobType: {
              elType: 'el-select',
              class: 'job-type-select',
              option: {
                delayLoadData: this.loadJobTypeList
              },
              props: { clearable: true },
              filterMethod: this.searchIndustryAndJobType
            }
          }
        },
        closeDate: {
          elType: 'el-date-picker',
          label: this.$t(`${this.i18nPrefix}.label.jobCloseDate`),
          rules: requiredRule,
          props: { clearable: false }
        },
        attractiveWords: {
          label: this.$t(`${this.i18nPrefix}.label.attractiveWords`),
          props: { maxLength: 20 }
        }
      }
      const isOverseasCompany = this.isOverseasCompany
      const allowEmployeeApplyJob = this.allowEmployeeApplyJob
      const companyIsELC = this.companyIsELC
      const hasCreateJobPermission = this.hasCreateJobPermission
      const hasCreateJobFromTemplatePermission = this.hasCreateJobFromTemplatePermission
      const showATSId = this.showATSId
      const hasCrmTagPermission = this.hasCrmTagPermission
      const hiddenList = []
      const disabledList = []

      if (isOverseasCompany) {
        hiddenList.push(lines.cnName)
        lines.enName.rules = requiredRule
      }
      if (!allowEmployeeApplyJob) {
        hiddenList.push(lines.visibility)
      }
      if (isTemplate || !hasCrmTagPermission) {
        hiddenList.push(lines.tag)
      }
      if (isTemplate) {
        hiddenList.push(
          lines.template,
          lines.creator,
          lines.atsId,
          lines.storeFront,
          lines.visibleOnlyMember,
          lines.location,
          lines.currencyAndSalary,
          lines.workTime,
          lines.industryAndJobType,
          lines.closeDate,
          lines.attractiveWords
        )
        _.forEach([
          lines.roleType,
          lines.experience,
          lines.requireExperience
        ], line => {
          Object.defineProperty(line, 'rules', {
            configurable: true,
            enumerable: true,
            get () {
              return vm.stepData.isEditableTemplate ? null : requiredRule
            }
          })
        })
      } else {
        if (hasCreateJobFromTemplatePermission && !hasCreateJobPermission) {
          disabledList.push(
            lines.enName,
            lines.cnName,
            lines.experience,
            lines.requireExperience
          )
        }
        hiddenList.push(lines.isEditableTemplate)
        if (!companyIsELC) {
          hiddenList.push(lines.storeFront)
        }
        if (!hasCreateJobFromTemplatePermission) {
          hiddenList.push(lines.template)
        }
        if (!showATSId) {
          hiddenList.push(lines.atsId)
        }
        if (isEditMode) {
          disabledList.push(
            lines.company,
            lines.requireExperience,
            lines.roleType
          )
          hiddenList.push(lines.template)
        }
      }
      _.forEach(hiddenList, line => {
        line.hidden = true
      })
      _.forEach(disabledList, line => {
        line.disabled = true
      })
      if (!this.isTemplate) {
        _.filter([
          lines.cnName,
          lines.enName,
          lines.requireExperience,
          lines.experience,
          lines.roleType
        ], line => {
          return !hiddenList.includes(line) && !disabledList.includes(line)
        }).forEach(line => {
          Object.defineProperty(line, 'disabled', {
            configurable: true,
            enumerable: true,
            get () {
              return vm.isNotEditableJobTemplate
            }
          })
        })
      }
      return lines
    }
  },
  created () {

  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('crm', ['searchTag']),
    ...mapActions('job', ['getJobExperienceList', 'getJobWorkTimeList', 'getJobSalaryList', 'getJobIndustryListWithJobType']),
    ...mapActions('company', ['getCompanyJobCreatorList', 'getCompanyStoreFrontList', 'getTeamMemberList']),
    ...mapActions('jobTemplate', ['getJobTemplateList']),
    ...mapActions('common', ['getCurrencyList']),
    formatSimpleJobInfo (infoItem) {
      const info = {
        ...infoItem,
        value: infoItem.id || infoItem.value
      }
      addI18nProperty(info, 'label', 'cn_name', 'name')
      return info
    },
    async loadCompanyJobTemplateList () {
      if (!this.companyJobTemplateListLoader) {
        this.companyJobTemplateListLoader = this.getJobTemplateList({ need_detail: 1, status: 1 }).then(resp => {
          const templateList = resp.result
          const companyMap = {}
          _.forEach(templateList, template => {
            addI18nProperty(template, 'label', 'cn_title', 'title')
            const company = template.company
            const companyId = template.company.id
            let companyInfo = companyMap[companyId]
            if (!companyInfo) {
              companyInfo = companyMap[companyId] = addI18nProperty({ ...company, templateList: [] }, 'label', 'cn_name', 'name')
            }
            companyInfo.templateList.push(template)
          })
          this.jobTemplateList = templateList
          this.companyJobTemplateList = Object.values(companyMap)
        })
      }
      await this.companyJobTemplateListLoader
      return this.companyJobTemplateList
    },
    loadCompanyList () {
      return this.$parent.$parent.$parent.loadCompanyList()
    },
    async loadCreatorList () {
      const resp = await this.getCompanyJobCreatorList(this.companyDataParams?.company_id || this.stepData.company)
      if (resp && resp.results) {
        this.creatorList = resp.results
      }
      if (this.isEditMode) {
        const jobCreator = this.jobInfo.created_account
        if (jobCreator && jobCreator.account_status === '0') {
          const disabledJobCreator = {
            text: this.$t(`${this.i18nPrefix}.label.emailIsDisabled`, { email: jobCreator.email_address }),
            searchtext: this.$t(`${this.i18nPrefix}.label.emailIsDisabled`, { email: jobCreator.email_address }),
            id: jobCreator.id,
            disabled: true
          }
          this.creatorList.push(disabledJobCreator)
        }
        const existedHr = _.find(this.creatorList, item => item.id === jobCreator.id)
        // 职位创建人不在能够选择的HR列表中，则只展示，不能选择
        if (!existedHr) {
          this.creatorList.push({
            text: jobCreator.email_address,
            searchtext: jobCreator.email_address,
            id: jobCreator.id,
            disabled: true
          })
        }
      }
      return this.creatorList
    },
    async loadJobMember () {
      const resp = await this.getTeamMemberList({ companyId: this.companyDataParams?.company_id || this.stepData.company })
      if (resp && resp.results) {
        this.jobMemberList = resp.results
      }
      if (this.isEditMode) {
        const jobMembers = this.jobInfo.job_members || []
        if (jobMembers.length > 0) {
          const disabledJobMembers = _.chain(jobMembers)
            .filter(item => item.status === '0')
            .map(item => {
              return {
                active: false,
                text: this.$t(`${this.i18nPrefix}.label.emailIsDisabled`, { email: item.email_address }),
                searchtext: this.$t(`${this.i18nPrefix}.label.emailIsDisabled`, { email: item.email_address }),
                id: item.id,
                name: '',
                disabled: true
              }
            })
            .value()
          this.jobMemberList.push(...disabledJobMembers)
          // 职位协作者不在能够选择的HR列表中，则只展示，不能选择
          const noExistedJobMembers = _.chain(jobMembers)
            .filter(item => !_.find(this.jobMemberList, member => member.id === item.id))
            .map(item => {
              return {
                active: false,
                text: item.email_address,
                searchtext: item.email_address,
                id: item.id,
                name: '',
                disabled: true
              }
            })
            .value()
          this.jobMemberList = _.concat(this.jobMemberList, noExistedJobMembers)
        }
      }
      return this.jobMemberList
    },
    async loadStoreFront () {
      const resp = await this.getCompanyStoreFrontList(this.companyDataParams)
      if (resp) {
        this.storeFrontList = _.map(resp, item => {
          return {
            ...item,
            label: `${item.identifier}  | ${item.name}`,
            value: item.id
          }
        })
      }
      return this.storeFrontList
    },
    async loadJobTagList () {
      const params = {
        is_company_tag: 1,
        tag_category: 'manual'
      }
      const resp = await this.searchTag({ ...this.companyDataParams, ...params })
      if (resp) {
        this.jobTagList = resp
      }
      return this.jobTagList
    },
    async loadExperienceList () {
      const resp = await this.getJobExperienceList()
      if (resp) {
        this.experienceList = _.map(resp, this.formatSimpleJobInfo)
      }
      return this.experienceList
    },
    async loadWorkTimeList () {
      const resp = await this.getJobWorkTimeList()
      if (resp) {
        const workTimeList = _.map(resp, this.formatSimpleJobInfo)
        workTimeList.splice(0, 0, createI18nProperty({ id: -1, value: -1 }, 'label', `${this.i18nPrefix}.label.workTimeNotSpecified`))
        this.workTimeList = workTimeList
      }
      return this.workTimeList
    },
    async loadCurrencyList () {
      const resp = await this.getCurrencyList()
      if (resp) {
        this.currencyList = _.map(resp, (item) => {
          return { id: item.id, label: `${item.name}(${item.value})`, value: item.name }
        })
      }
      return this.currencyList
    },
    async loadSalaryList () {
      const resp = await this.getJobSalaryList()
      if (resp) {
        this.salaryList = _.map(resp, this.formatSimpleJobInfo)
      }
      return this.salaryList
    },
    async loadIndustryList () {
      if (!this.industryListLoader) {
        this.industryListLoader = this.getJobIndustryListWithJobType().then(resp => {
          if (resp) {
            this.industryList = _.map(resp, (item) => {
              const industry = this.formatSimpleJobInfo(item)
              industry.children = _.map(industry.children, this.formatSimpleJobInfo)
              return industry
            })
          }
        })
      }
      await this.industryListLoader
      return this.industryList
    },
    async searchIndustryAndJobType (keywords, dataList) {
      if (keywords) {
        return _.filter(dataList, data => _.find([data.name, data.cn_name, data.pinyin_name], value => value.includes(keywords)))
      } else {
        return dataList
      }
    },
    async loadJobTypeList () {
      if (!this.stepData.industry) {
        return []
      }
      const industryList = await this.loadIndustryList()
      const industry = _.find(industryList, { id: this.stepData.industry })
      return industry.children
    },
    handleAddTagClick () {
      window.open('/v-admin/crm-management/tag/add')
    },
    handleTemplateChange (templateId) {
      let template = { id: null, job_property: null }
      if (templateId) {
        template = _.find(this.jobTemplateList, { id: templateId })
      }
      this.stepData = {
        ...this.stepData,
        cnName: template.cn_title,
        enName: template.title,
        visibility: _.isUndefined(template.job_visibility) ? 'IE' : template.job_visibility,
        requireExperience: template.experience_required === 'Y',
        roleType: template.role_type,
        experience: template.experience
      }
    },
    cloneTemplateQuestions (questions = []) {
      const result = cloneSelectedQuestionSubtrees({
        selectedRoots: questions,
        sourceQuestions: questions,
        generateTempVid: () => _.uniqueId('job_template_question_')
      })
      if (result.errors) {
        const message = this.$t('admin.openQuestion.message.invalidTemplateTree')
        this.$message.error(message)
        throw new Error(message)
      }
      return result.questions
    },
    async setStepDataToJobInfo () {
      const stepData = this.stepData
      const jobInfo = this.jobInfo
      this.$set(jobInfo, 'company', { id: stepData.company })
      this.$set(jobInfo, 'created_account', { id: stepData.creator })
      this.$set(jobInfo, 'job_members', _.map(stepData.jobMember, memberId => { return { id: memberId } }))
      this.$set(jobInfo, 'job_store_front', { id: stepData.storeFront })
      this.$set(jobInfo, 'cn_title', stepData.cnName)
      this.$set(jobInfo, 'title', stepData.enName)
      this.$set(jobInfo, 'job_visibility', stepData.visibility)
      this.$set(jobInfo, 'ats_job_id', stepData.atsId)
      this.$set(jobInfo, 'job_tags', _.map(stepData.tag, tagId => { return { id: tagId } }))
      this.$set(jobInfo, 'visible_only_member', stepData.visibleOnlyMember)
      this.$set(jobInfo, 'experience_required', stepData.requireExperience ? 'Y' : 'N')
      this.$set(jobInfo, 'role_type', stepData.roleType)
      this.$set(jobInfo, 'job_locations', stepData.location)
      this.$set(jobInfo, 'experience', stepData.experience)
      this.$set(jobInfo, 'currency', stepData.currency)
      this.$set(jobInfo, 'compensation', { id: stepData.salary, item: _.find(this.salaryList, { id: stepData.salary }) })
      this.$set(jobInfo, 'work_time', { id: stepData.workTime, item: _.find(this.workTimeList, { id: stepData.workTime }) })
      this.$set(jobInfo, 'job_industry', { id: stepData.industry || null })
      this.$set(jobInfo, 'job_type', { id: stepData.jobType || null })
      this.$set(jobInfo, 'expire_date', moment(stepData.closeDate).valueOf())
      this.$set(jobInfo, 'attractive_words', stepData.attractiveWords)
      if (this.isTemplate) {
        this.$set(jobInfo, 'job_property', stepData.isEditableTemplate ? '1' : '0')
      } else if (!this.isEditMode) {
        let templateInfo = { id: null, job_property: null }
        if (stepData.template) {
          await this.loadCompanyJobTemplateList()
          templateInfo = _.find(this.jobTemplateList, { id: stepData.template })
        }
        const oldTemplateId = jobInfo.job_template?.id || null
        if (oldTemplateId !== templateInfo.id) {
          const prescreenQuestions = this.cloneTemplateQuestions(templateInfo.prescreen_questions || [])
          const questions = this.cloneTemplateQuestions(templateInfo.questions || [])
          const hrQuestions = this.cloneTemplateQuestions(templateInfo.hr_questions || [])
          this.$set(jobInfo, 'cn_description', templateInfo.cn_description)
          this.$set(jobInfo, 'description', templateInfo.description)
          this.$set(jobInfo, 'resume_type', templateInfo.resume_type)
          this.$set(jobInfo, 'required_resume_attachment', templateInfo.required_resume_attachment)
          this.$set(jobInfo, 'assessment_value_required', templateInfo.assessment_value_required)
          this.$set(jobInfo, 'assessment_occupational_required', templateInfo.assessment_occupational_required)
          this.$set(jobInfo, 'assessment_additional_required', templateInfo.assessment_additional_required)
          this.$set(jobInfo, 'personality_required', templateInfo.personality_required)
          this.$set(jobInfo, 'personal_assessment_type', templateInfo.personal_assessment_type)
          this.$set(jobInfo, 'problem_solving_required', templateInfo.problem_solving_required)
          this.$set(jobInfo, 'prescreen_questions', prescreenQuestions)
          this.$set(jobInfo, 'questions', questions)
          this.$set(jobInfo, 'hr_questions', hrQuestions)
          this.$set(jobInfo, 'culture', templateInfo.culture)
          this.$set(jobInfo, 'video_navigation_list', templateInfo.video_navigation_list)
        }
        this.$set(jobInfo, 'job_template', templateInfo)
      }
    },
    async getStepData () {
      const jobInfo = this.jobInfo
      let creatorId = jobInfo.created_account?.id
      if (!this.isEditMode && !this.isTemplate && !creatorId) {
        const currentAccount = this.getCurrentAccount()
        creatorId = currentAccount.id
      }
      const stepData = {
        isEditableTemplate: true,
        template: jobInfo.job_template?.id,
        company: jobInfo.company?.id,
        creator: creatorId,
        jobMember: _.map(jobInfo.job_members, 'id'),
        storeFront: jobInfo.job_store_front?.id,
        cnName: jobInfo.cn_title,
        enName: jobInfo.title,
        visibility: jobInfo.job_visibility || VISIBILITY_TYPE.IE,
        atsId: jobInfo.ats_job_id,
        tag: _.map(jobInfo.job_tags, 'id'),
        visibleOnlyMember: jobInfo.visible_only_member || false,
        requireExperience: typeof jobInfo.experience_required === 'string' ? jobInfo.experience_required === 'Y' : true,
        roleType: jobInfo.role_type || ROLE_TYPE.FULL_TIME,
        location: jobInfo.job_locations || [],
        experience: jobInfo.experience,
        currency: jobInfo.currency,
        salary: jobInfo.compensation?.id,
        workTime: jobInfo.work_time?.id || 2,
        industry: jobInfo.job_industry?.id,
        jobType: jobInfo.job_type?.id,
        closeDate: jobInfo.expire_date && moment(jobInfo.expire_date).valueOf(),
        attractiveWords: jobInfo.attractive_words
      }
      if (!this.isEditMode) {
        const companyList = await this.loadCompanyList()
        if (!stepData.company) {
          const queryCompanyId = parseInt(this.$route.query.company_id) || null
          if (queryCompanyId && _.findIndex(companyList, { id: queryCompanyId }) > -1) {
            stepData.company = queryCompanyId
          } else {
            stepData.company = this.company.id
          }
        }
      }
      if (this.isTemplate) {
        stepData.isEditableTemplate = jobInfo.job_property ? jobInfo.job_property === '1' : true
      }
      return stepData
    }
  },
  watch: {

  }
}
</script>

<style lang="scss" scoped>
.job-base-info {
  .job-location-editor {
    line-height: normal;

    ::v-deep {
      .table-list-wrapper {
        .filter-group {
          .el-input {
            .el-input__inner {
              border-color: #b2b2b2;

              &:focus {
                border-color: #b2b2b2;
              }

              &:hover {
                border-color: #b2b2b2;
              }
            }
          }
        }
      }

      .location-editor-container {
        .location-info {
          .el-input {
            .el-input__inner {
              border-color: #b2b2b2;

              &:focus {
                border-color: #b2b2b2;
              }

              &:hover {
                border-color: #b2b2b2;
              }
            }
          }
        }
      }
    }
  }

  ::v-deep {
    .job-tag-line {
      .el-form-item__content {
        display: flex;
        flex-direction: column;
      }

      .add-tag-btn {
        margin-left: auto;
        margin-right: 0;
        padding-top: 5px;
        padding-bottom: 5px;
      }
    }

    .multiple-line.salary-form-line {
      .multiple-line-item.currency-select {
        width: 30%;
      }

      .multiple-line-item.salary-select {
        width: 70%;
      }
    }
  }
}
</style>
